import { motion } from "framer-motion";
import { X, Save, Film } from "lucide-react";

import { absoluteUrl } from "@/lib/uploads";
import { useVideoEditor } from "@/components/video-editor/useVideoEditor";
import TrimControls from "@/components/video-editor/TrimControls";
import CaptionsPanel from "@/components/video-editor/CaptionsPanel";
import CoverPanel from "@/components/video-editor/CoverPanel";

/**
 * VideoEditorModal — AI video editing studio (trim + captions + cover).
 *
 * Behaviour split into modules under `components/video-editor/`:
 *   - useVideoEditor (hook): state + every save/generate/trim handler
 *   - TrimControls / CaptionsPanel / CoverPanel (UI parts)
 *
 * This file is the modal shell + footer action bar only.
 */
export default function VideoEditorModal({ post, onClose, onSaved }) {
  const ed = useVideoEditor(post, { onClose, onSaved });

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.5)", backdropFilter: "blur(10px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-heavy rounded-3xl max-w-4xl w-full max-h-[92vh] overflow-y-auto grain p-6"
        data-testid="video-editor-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#F5C542]">video editor</div>
            <h3 className="font-display text-2xl font-bold mt-1">AI editing studio</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Video preview + trim */}
          <div className="lg:col-span-2 space-y-4">
            <div className="relative rounded-2xl overflow-hidden bg-black">
              <video
                ref={ed.videoRef}
                src={absoluteUrl(post.video_url)}
                controls
                className="w-full max-h-[420px]"
                data-testid="editor-video"
              />
              {ed.captions && (
                <div className="absolute bottom-12 left-0 right-0 text-center px-4">
                  <span
                    className="inline-block bg-black/70 text-white px-3 py-1.5 rounded-md text-sm font-medium"
                    style={{ textShadow: "0 2px 8px rgba(0,0,0,0.6)" }}
                  >
                    {ed.captions}
                  </span>
                </div>
              )}
            </div>
            <TrimControls
              duration={ed.duration}
              startSec={ed.startSec}
              endSec={ed.endSec}
              onStartChange={ed.setStartSec}
              onEndChange={ed.setEndSec}
              onPreview={ed.previewSegment}
            />
          </div>

          {/* Right column: captions + cover */}
          <div className="space-y-4">
            <CaptionsPanel
              captions={ed.captions}
              onChange={ed.setCaptions}
              onGenerate={ed.generateCaptions}
              busy={ed.genCap}
            />
            <CoverPanel
              coverUrl={ed.coverUrl}
              onGenerate={ed.generateCover}
              busy={ed.genCover}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2 flex-wrap">
          <button onClick={onClose} className="nexus-btn-secondary text-sm">Cancel</button>
          <button
            data-testid="editor-render"
            onClick={ed.renderTrim}
            disabled={ed.trimming || ed.saving}
            className="nexus-btn-secondary text-sm flex items-center gap-2"
          >
            <Film size={14} /> {ed.trimming ? "Encoding…" : "Render trim (ffmpeg)"}
          </button>
          <button
            data-testid="editor-save"
            onClick={ed.save}
            disabled={ed.saving || ed.trimming}
            className="nexus-btn-primary text-sm flex items-center gap-2"
          >
            <Save size={14} /> {ed.saving ? "…" : "Save edits"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
